/* eslint-disable jira/integration/test-id-by-folder-structure */
/* eslint-disable jira/naming/disallow-filenames */
/** @jsx jsx */
// eslint-disable-next-line jira/filename-case
import React, { useMemo, useState, useCallback, type ReactNode } from 'react';
import { cssMap, jsx } from '@compiled/react';
import flatten from 'lodash/flatten';
import noop from 'lodash/noop';
import EnterEscapeHandler from '@jira/platform__enter-escape-handler/src/index.tsx';
import Avatar from '@atlaskit/avatar/avatar';
import Field, { type FieldProps } from '@atlaskit/form/field';
import { ErrorMessage } from '@atlaskit/form/error-message';
import Flex from '@atlaskit/primitives/components/flex';
import Stack from '@atlaskit/primitives/stack';
import Text from '@atlaskit/primitives/text';
import { xcss } from '@atlaskit/primitives/xcss';
import Select from '@atlaskit/select/default';
import { components } from '@atlaskit/react-select/components';
import type { SingleValueProps, ValueType } from '@atlaskit/select/types';
import { token } from '@atlaskit/tokens';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { BoardCreateFormValues, SourceTypeOption } from './BoardCreateFormValues.types.tsx';
import { useBoardCreateContext } from './useBoardCreateContext.tsx';
import { getOptionGroups } from './getOptionGroups.tsx';

const selectPlaceholder = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.select-placeholder',
	defaultMessage: 'Select',
	description: 'Placeholder for the source type selector',
});

const sourceTypeFieldLabel = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.source-type-field-label',
	defaultMessage: 'Choose what to include in this board',
	description:
		'Label for the field which asks the user to select sources for the new board they are creating',
});

const sourceTypePickerAriaLabel = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.source-type-picker-aria-label',
	defaultMessage: 'Select what to include in this board',
	description: 'Label for the source type selector',
});

const sourceTypeEmptyError = defineMessage({
	id: 'software-board-create.modal.form.source-type-picker.source-type-empty-error',
	defaultMessage: 'Select what to include in this board',
	description:
		'Error message for board source type telling the user that board source is required to create a board',
});

export const VALIDATION_ERROR_EMPTY = 'VALIDATION_ERROR_EMPTY';

const formatOptionLabel = (option: SourceTypeOption) => {
	const { icon: Icon, title, description } = option;

	return (
		<Flex alignItems="center" gap="space.150" xcss={optionStyles}>
			{typeof Icon === 'string' ? (
				<Avatar src={Icon} size="xxsmall" appearance="square" borderColor="transparent" />
			) : (
				<Icon label="" name="projectIcon" />
			)}
			<Stack space="space.050">
				<Text>{title}</Text>
				{description && (
					<Text size="small" color="color.text.subtlest">
						{description}
					</Text>
				)}
			</Stack>
		</Flex>
	);
};

const SingleValue = (props: SingleValueProps<SourceTypeOption>) => {
	const {
		data: { selectedTitle, icon: Icon },
	} = props;

	const { formatMessage } = useIntl();

	return (
		<components.SingleValue {...props}>
			<Flex alignItems="center" gap="space.100">
				{typeof Icon === 'string' ? (
					<Avatar src={Icon} size="xxsmall" appearance="square" name="avatar" />
				) : (
					<Icon />
				)}
				<Text>
					{formatMessage(selectedTitle, {
						strong: (children: ReactNode) => <Text as="strong">{children}</Text>,
					})}
				</Text>
			</Flex>
		</components.SingleValue>
	);
};

export const useSelectedOption = (): [
	'team' | 'issueTypes' | 'labels' | 'savedFilter' | null | undefined,
	React.Dispatch<
		React.SetStateAction<'team' | 'issueTypes' | 'labels' | 'savedFilter' | null | undefined>
	>,
] => useState<BoardCreateFormValues['sourceType'] | null>();

const controlStyles = cssMap({
	focused: {
		'&:focus-within': {
			boxShadow: `inset 0px 0px 0px 1px ${token('color.border.focused')}`,
		},
		'&:hover': {
			borderColor: token('color.border.focused'),
			boxShadow: token('elevation.shadow.raised'),
		},
	},
});

const groupStyles = cssMap({
	root: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:not(:last-of-type)': {
			borderBottomWidth: token('border.width.selected'),
			borderBottomStyle: 'solid',
			borderBottomColor: token('color.border'),
		},
	},
});

export const SourceTypePicker = ({
	resetSourceValue,
}: {
	resetSourceValue: () => void;
}): JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [selected, setSelected] = useSelectedOption();
	const { projectAvatar } = useBoardCreateContext();
	const optionGroups = useMemo(
		() => getOptionGroups({ formatMessage, projectAvatar }),
		[formatMessage, projectAvatar],
	);

	const flattenedOptions = useMemo(
		() => flatten(optionGroups.map((group) => group.options)),
		[optionGroups],
	);

	const handleChange = useCallback(
		(onFieldChange: FieldProps<BoardCreateFormValues['sourceType']>['onChange']) =>
			(value: ValueType<SourceTypeOption, false>) => {
				const option = value?.value || null;
				resetSourceValue();
				setSelected(option);
				onFieldChange(option);
				fireUIAnalytics(
					createAnalyticsEvent({
						actionSubject: 'select',
						action: 'changed',
					}),
					'boardSourceType',
					{ sourceType: option },
				);
			},
		[createAnalyticsEvent, resetSourceValue, setSelected],
	);

	const handleOnMenuOpen = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				actionSubject: 'select',
				action: 'opened',
			}),
			'boardSourceType',
		);
	}, [createAnalyticsEvent]);

	return (
		<Field<BoardCreateFormValues['sourceType']>
			name="sourceType"
			label={formatMessage(sourceTypeFieldLabel)}
			defaultValue={undefined}
			isRequired
		>
			{({ fieldProps: { onChange: onFieldChange, ...rest }, error }) => (
				<>
					{/* Prevent ESC press on Select bubbling up to the modal */}
					<EnterEscapeHandler onEscape={noop}>
						<Select<SourceTypeOption, false>
							{...rest}
							aria-label={formatMessage(sourceTypePickerAriaLabel)}
							options={optionGroups}
							isSearchable={false}
							formatOptionLabel={formatOptionLabel}
							components={{
								SingleValue,
								Control: (props) => (
									<components.Control {...props} xcss={props.isFocused && controlStyles.focused} />
								),
								Group: (props) => <components.Group {...props} xcss={groupStyles.root} />,
							}}
							placeholder={formatMessage(selectPlaceholder)}
							menuPosition="fixed"
							classNamePrefix="source-type-picker"
							onChange={handleChange(onFieldChange)}
							value={flattenedOptions.find((option) => option.value === selected)}
							// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
							styles={{
								control: (base, state) => ({
									...base,
									borderColor: state.isFocused ? token('color.border.focused') : base.borderColor,
									':focus-within': {
										boxShadow: state.isFocused
											? `inset 0px 0px 0px 1px ${token('color.border.focused')}`
											: base.boxShadow,
									},
									':hover': {
										borderColor: state.isFocused ? token('color.border.focused') : base.borderColor,
										boxShadow: state.isFocused ? 'elevation.shadow.raised' : base.boxShadow,
									},
								}),
								option: (base) => ({
									...base,
									// padding inside the option so hover covers the element
									padding: `${token('space.100')} 0 ${token('space.100')} ${token('space.300')}`,
								}),
								group: (base) => ({
									...base,
									padding: 0,
									':not(:last-of-type)': {
										borderBottomWidth: token('border.width.selected'),
										borderBottomStyle: 'solid',
										borderBottomColor: token('color.border'),
									},
								}),
								groupHeading: (base) => ({
									...base,
									margin: 0,
								}),
								menuList: (base) => ({
									...base,
									padding: 0,
									maxHeight: 325,
								}),
							}}
							onMenuOpen={handleOnMenuOpen}
						/>
					</EnterEscapeHandler>
					{error === VALIDATION_ERROR_EMPTY && (
						// eslint-disable-next-line jira/integration/test-id-by-folder-structure
						<ErrorMessage testId="software-board-create.ui.modal.form.source-type-picker.error">
							{formatMessage(sourceTypeEmptyError)}
						</ErrorMessage>
					)}
				</>
			)}
		</Field>
	);
};

const optionStyles = xcss({
	minHeight: '40px',
});
